export const teamCollaborationModule = {
  id: "team-collaboration-agile",
  title: "Agile Team Collaboration & Communication",
  description: "Build high-performing Agile teams through effective collaboration practices and communication strategies.",
  content: `
## What You'll Learn
By the end of this module, you will be able to:
- Foster effective collaboration within cross-functional Agile teams
- Implement communication practices that enhance team productivity
- Resolve conflicts and build consensus in Agile environments
- Create psychological safety for innovation and continuous improvement

## Prerequisites
- Understanding of Agile principles and Scrum framework
- Experience working in team environments
- Basic knowledge of group dynamics

## Overview
High-performing Agile teams are 5x more productive than average teams. This module teaches collaboration and communication techniques that create the foundation for exceptional team performance.

---

## Key Concepts

### Cross-Functional Team Dynamics

#### Team Formation Stages
**Forming**: Team members get acquainted, establish ground rules
**Storming**: Conflicts emerge as different working styles clash
**Norming**: Team develops shared practices and agreements
**Performing**: Team operates efficiently with minimal friction

#### T-Shaped Professionals
**Deep Expertise**: Strong skills in primary discipline
**Broad Knowledge**: Understanding of other team disciplines
**Collaboration Skills**: Ability to work across functional boundaries

### Communication Practices

#### Information Radiators
**Sprint Burndown Charts**: Visual progress tracking
**Definition of Done Posters**: Shared quality standards
**Team Working Agreements**: Collaborative norms and expectations
**Impediment Boards**: Transparent problem visibility

#### Facilitation Techniques
**Timeboxing**: Respect for meeting duration limits
**Round Robin**: Ensure equal participation
**Dot Voting**: Democratic prioritization method
**Silent Brainstorming**: Generate ideas without groupthink

---

## Practical Examples

### Example 1: Team Working Agreements
Establish clear expectations for team collaboration.

**Working Agreement Categories**:
- Core hours and availability expectations
- Communication channels and response times  
- Meeting participation and preparation standards
- Code review and quality practices
- Conflict resolution approaches

**Sample Working Agreement**:
"We agree to be available for collaboration during core hours (10 AM - 3 PM). We commit to responding to Slack messages within 4 hours during business days. We will come prepared to all ceremonies and actively participate in discussions."

### Example 2: Conflict Resolution
Handle disagreements constructively using Agile principles.

**Conflict Resolution Process**:
1. Address conflicts early before escalation
2. Focus on positions vs. underlying interests
3. Seek win-win solutions that benefit the team
4. Use data and user feedback to guide decisions
5. Escalate to Scrum Master or coach when needed

---

## Assessment

This module builds the interpersonal skills necessary for Agile team success and sustainable high performance.
`,
  duration: 45
};